import { useLocation } from 'react-router-dom';
import { Avatar, Box, Group, Image, Text } from '@mantine/core';
import { useAuthStore } from '../../stores/auth';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

/**
 * MED-1010 / MED-1068 — the reduced in-session header (Medley wordmark + the
 * signed-in user's identity, no navigation). Extracted from `InSessionLayout`
 * so the same reduced header can render beneath the Session Preview bar
 * (MED-1068 AC1) without dragging the full app chrome along.
 *
 * Role-aware: coach slate vs. peer-leader green background and role label,
 * matching the user's normal app chrome. `contextOverride` lets a caller (the
 * preview / dev routes) pick the variant independently of the URL, since those
 * routes don't carry the `/participant/` prefix the live route detects.
 *
 * Presentational only — the wordmark is intentionally non-clickable and there
 * are no menu actions (an active session offers no navigation away; the page
 * owns its only intended exit).
 */
export interface InSessionHeaderBarProps {
  contextOverride?: 'coach' | 'participant';
}

export function InSessionHeaderBar({
  contextOverride,
}: InSessionHeaderBarProps = {}) {
  const location = useLocation();
  const isParticipantContext = contextOverride
    ? contextOverride === 'participant'
    : location.pathname.startsWith('/participant/');

  const { user } = useAuthStore();
  const isPartnerCoach = user?.coach_type === 'partner';
  const userInitials = user?.initials || user?.first_name?.charAt(0) || 'U';
  const userName =
    user?.display_name ||
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
    'User';
  // Coaches carry an explicit role label; peer leaders surface their job title
  // (mirrors ParticipantLayout).
  const userRole = isParticipantContext
    ? user?.job_title || ''
    : isPartnerCoach
      ? 'Group Leader'
      : 'Executive Coach';

  // Coach slate vs. peer-leader green, matching the user's normal app chrome.
  const headerBg = isParticipantContext ? '#2D4739' : '#2D3748';

  return (
    <Box style={{ backgroundColor: headerBg }}>
      <Group h={68} px={32} justify="space-between" wrap="nowrap">
        {/* Left: non-clickable Medley wordmark only — no navigation. */}
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          <Image
            src={medleyLogoWhite}
            alt="Medley"
            h={24}
            w="auto"
            fit="contain"
          />
        </Box>

        {/* Right: signed-in user identity only — no menu, no actions. */}
        <Group gap="sm" wrap="nowrap">
          <Avatar
            size={34}
            radius="xl"
            color={isParticipantContext ? 'medleyGreen' : 'coachNavy'}
            src={user?.avatar_url}
          >
            {userInitials}
          </Avatar>
          <Box visibleFrom="sm">
            <Text fz={16} fw={600} c="white" lh={1.2}>
              {userName}
            </Text>
            {userRole && (
              <Text fz={11} fw={500} c="rgba(255,255,255,0.7)" lh={1.2}>
                {userRole}
              </Text>
            )}
          </Box>
        </Group>
      </Group>
    </Box>
  );
}

export default InSessionHeaderBar;
